// src/lib/socketInterface.js Shared data channel message handlers and utilities
import { clearRemoteSamples, receiveSamples, setSamplesOnWire } from '~/lib/automation'
import { receiveTempo } from '~/lib/clock/tempoSync'
import store from '~/store'
import { setCurrentPresetIndex, setPlaybackState, sharedPlayback } from '~/store/playback'
import { selectPlaylist } from '~/store/playback/selectors'
import { loadPreset } from '~/store/playback/thunks'
import { selectIsRemote, selectIsRunner } from '~/store/app/selectors'
import {
	setActiveDevice,
	setAdvancedControls,
	setDCConnections,
	setOutputDevices,
	setNetworkQuality,
	setPendingOutputs,
	setRunnerDevice,
	setSessionState,
	setSourceQuality,
	setVideoQuality,
} from '~/store/app'
import { patchModulators } from '~/store/live'
import { ABLY_CLIENT_ID, DC_MESSAGES, IS_MOBILE } from './constants'
import { applyInboundParams } from './inboundParams'
import { showErrorDialog } from '~/store/app/thunks'
import { isLiveSyncHeld, mergeSnapshot, releaseLiveSync } from '~/store/middleware/liveSync'
import { pushError } from '~/store/ui'
import socket from './socket'
import { receive as receiveLink, startDesktopLink } from './desktopLink'
import { startRemoteFeed } from './remoteState'

const logger = console.scope('socketInterface')

// Shared data channel message handler
const handleDCMessage = (type, payload) => {
	// Link frames arrive at gesture rate.
	// if (type !== DC_MESSAGES.LINK) logger.info('Data channel message', { type, payload })

	switch (type) {
		case DC_MESSAGES.PRESENCE: {
			const activeDevice = payload.active_connection_name
			const isMe = activeDevice === ABLY_CLIENT_ID
			logger.info('🔍 PRESENCE debug', {
				activeDevice,
				myId: ABLY_CLIENT_ID,
				isMe,
				match: isMe ? '✅ MATCH' : '❌ NO MATCH',
			})
			store.dispatch(setActiveDevice(activeDevice))
			if ('runner_connection_name' in payload) {
				setSamplesOnWire(true)
				// The role moved: the last runner's samples come off every peer, desktop or mobile.
				if (payload.runner_connection_name !== store.getState().app.runner_device) clearRemoteSamples(store)
				store.dispatch(setRunnerDevice(payload.runner_connection_name))
			}
			if (payload.connections) {
				store.dispatch(setDCConnections(payload.connections))
			}
			if (payload.output_devices) {
				store.dispatch(setOutputDevices(payload.output_devices))
			}
			if (payload.pending_outputs) {
				store.dispatch(setPendingOutputs(payload.pending_outputs))
			}
			startRemoteFeed()
			startDesktopLink()
			break
		}
		case DC_MESSAGES.PARAMETERS: {
			// Syncs live parameters sent from other clients, linked to the liveSync middleware.
			if (payload.is_default && !selectIsRemote(store.getState())) {
				logger.info("I am first client, setting server's initial parameters")
				// The first client scenario needs to be handled by the specific socket implementation since we need access to the socket instance to call sendDC This is handled in the socket's onmessage handler
				return
			}
			// Only a join snapshot carries `is_default`; its loops are the session's whole set, so any this client holds that it lacks are dropped.
			const snapshot = 'is_default' in payload
			delete payload.is_default
			const modulators =
				snapshot && payload.modulators
					? {
							...Object.fromEntries(
								Object.keys(store.getState().live.modulators ?? {}).map(p => [p, null])
							),
							...payload.modulators,
						}
					: payload.modulators
			delete payload.modulators

			// Server nests objects inside diffusion; unnest to match client shape
			if (payload.diffusion?.objects) {
				if (!payload.objects) payload.objects = payload.diffusion.objects
				delete payload.diffusion.objects
			}

			// Session state, on its own key — fan into the SETTINGS slice, never `live`, since it is not preset state. `meta.sync` stops the settings listener echoing it back.
			//
			// Removed from `payload` before the live dispatch below: `live` has no such effect, and leaving it would write session state into the preset shape.
			if (payload.server_settings) {
				for (const [key, value] of Object.entries(payload.server_settings)) {
					store.dispatch({
						type: 'settings/setSettingsProperty',
						payload: [key, value],
						meta: { sync: true },
					})
				}
				delete payload.server_settings
			}

			const rejoin = snapshot && isLiveSyncHeld()
			const overridden = rejoin
				? mergeSnapshot(payload, modulators, store.getState().live, selectIsRunner(store.getState()))
				: 0

			// Delay applying parameters if mouse is down.
			//
			// ⚠️ MERGED, never overwritten. During a peer's fade these arrive at frame rate, so a second message inside one drag would drop the first entirely. Per key: a `null` or non-object section REPLACES — `null` is the effect-removal sentinel and must win over any section it follows — while an object section shallow-merges over the pending one. `objects` merges as a section too: its per-id nulls are values inside it and survive. `server_settings` is already stripped above, so pending only ever holds params plus objects.
			if (window.mouse_down) {
				const prev = window.pending_params
				if (!prev) window.pending_params = payload
				else {
					for (const key in payload) {
						const next = payload[key]
						const held = prev[key]
						prev[key] =
							next === null ||
							typeof next !== 'object' ||
							Array.isArray(next) ||
							!held ||
							typeof held !== 'object'
								? next
								: { ...held, ...next }
					}
				}
			} else {
				applyInboundParams(payload)
			}
			// After the params, so a loop on an object this payload adds finds it — held with them mid-drag, merged per path so a later `null` still drops a loop.
			if (modulators && typeof modulators === 'object') {
				if (window.mouse_down) window.pending_modulators = { ...window.pending_modulators, ...modulators }
				else
					store.dispatch({
						type: patchModulators.type,
						payload: modulators,
						meta: { sync: true, skipHistory: true },
					})
			}
			if (rejoin) {
				releaseLiveSync(store.getState().live)
				if (overridden) {
					store.dispatch(
						pushError({
							code: 'RECONNECT_OVERRIDDEN',
							title: 'Reconnected',
							message: `${overridden} change${overridden > 1 ? 's' : ''} made while offline ${overridden > 1 ? 'were' : 'was'} replaced by the live session.`,
						})
					)
					store.dispatch(showErrorDialog())
				}
			}
			break
		}
		case DC_MESSAGES.SAMPLES:
			receiveSamples(store, payload)
			break
		case DC_MESSAGES.LINK:
			receiveLink(payload)
			break
		case DC_MESSAGES.TEMPO:
			receiveTempo(payload)
			break
		case DC_MESSAGES.PLAYBACK:
			store.dispatch(setPlaybackState(sharedPlayback(payload)))
			break
		case DC_MESSAGES.CUE: {
			if (!payload?.id) break
			const s = store.getState()
			// By id, never by index: the phone plays its own project, and a stale cache reorders, so the same index here is another look. A preset this client lacks is not followed.
			const index = selectPlaylist(s).findIndex(p => p?.id === payload.id)
			if (index < 0) break
			// The index only, for the runner's autoplay step and on a stage-less client: the sender's live patch already carries the picture, and a load here would start a second fade on the wire.
			if (payload.run || IS_MOBILE) {
				if (!selectIsRunner(s)) store.dispatch(setCurrentPresetIndex(index))
			} else store.dispatch(loadPreset({ index, instant: payload.instant, sync: true }))
			break
		}
		case DC_MESSAGES.SESSION_STATE:
			store.dispatch(setSessionState(payload))
			break
		case DC_MESSAGES.ERROR:
			logger.error(`Data error code: ${payload.code}`, payload)
			// TODO: handle
			break
		case DC_MESSAGES.SHUTDOWN:
			logger.warn('Server shutdown:', payload?.reason)
			if (payload?.reason === 'kicked') {
				socket._kicked = true
				socket.stopWebRTC()
			}
			break
		case DC_MESSAGES.VIDEO_QUALITY:
			store.dispatch(setVideoQuality(payload))
			break
		case DC_MESSAGES.NETWORK_QUALITY:
			store.dispatch(setNetworkQuality({ ...payload, at: Date.now() }))
			break
		case DC_MESSAGES.SOURCE_QUALITY:
			store.dispatch(setSourceQuality({ ...payload, at: Date.now() }))
			break
		case DC_MESSAGES.SCHEME:
			store.dispatch(setAdvancedControls(payload))
			break
		default:
			logger.error('Unknown message type', { type, payload })
	}
}

export default handleDCMessage
